import { Component, Inject, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { ChildrenSelectorComponent, Student } from '../../shared/children-selector/children-selector.component';
import { CarnetService, CarnetItem } from '../../services/carnet.service';
import { PageTitleComponent } from '../../shared/page-title/page-title.component';
import { LoaderComponent } from "../../shared/loader/loader.component";
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

@Component({
  selector: 'app-carnet',
  standalone: true,
  imports: [CommonModule, ChildrenSelectorComponent, TranslatePipe, PageTitleComponent, LoaderComponent],
  templateUrl: './carnet.component.html',
  styleUrl: './carnet.component.css'
})
export class CarnetComponent {
  userData: any;
  students: Student[] = [];
  selectedStudent: Student | null = null;
  digiref: number | null = null;

  loading = false;
  error: string | null = null;
  isLoading = false; // ← loader
  // Backend data grouped by trimester and type
  carnetsByTrimester: Record<string, CarnetItem[]> = {
    premiere: [],
    deuxieme: [],
    troisieme: [],
    annexe: []
  };

  // Organized data by trimester and document type
  organizedCarnets: Record<string, Record<string, CarnetItem[]>> = {
    premiere: { carnet_prive: [], carnet_etatique: [], certificat_prive: [], certificat_etatique: [], bulletin: [] },
    deuxieme: { carnet_prive: [], carnet_etatique: [], certificat_prive: [], certificat_etatique: [], bulletin: [] },
    troisieme: { carnet_prive: [], carnet_etatique: [], certificat_prive: [], certificat_etatique: [], bulletin: [] },
    annexe: { annexe_prive: [], annexe_etatique: [] }
  };

  // Modal state
  showModal = false;
  modalItem: CarnetItem | null = null;
  modalHtml: SafeHtml | null = null;
  modalError: string | null = null;
  modalLoading = false;

  constructor(
    private carnetService: CarnetService,
    @Inject(PLATFORM_ID) private platformId: Object,
    private translate: TranslateService,
    private sanitizer: DomSanitizer
  ) {}

  onStudentsLoaded(students: Student[]) {
    this.students = students;
    if (!this.selectedStudent && students.length > 0) {
      this.onStudentSelected(students[0]);
    }
  }

  onStudentSelected(student: Student) {
    this.selectedStudent = student;
    this.digiref = student.digiref !== undefined ? student.digiref : null;
    this.fetchCarnets();
  }
fetchCarnets(page: number = 1) {
  if (!this.selectedStudent) return;
  if (!this.userData && isPlatformBrowser(this.platformId)) {
    const userDataStr = localStorage.getItem('userData');
    if (userDataStr) {
      try { this.userData = JSON.parse(userDataStr); } catch {}
    }
  }
  if (!this.userData) return;

  this.isLoading = true; // ← ici
  this.error = null;

  const s = this.selectedStudent;
  this.carnetService.getStudentCarnets(
    s.nameOrg,
    s.gradeId,
    s.batchId,
    s.idStudent,
  ).subscribe({
    next: (items) => {
      this.groupByTrimester(items || []);
      this.isLoading = false; // ← loader off
    },
    error: (err) => {
      console.error('Failed to load carnets', err);
      this.error = 'Failed to load carnets';
      this.isLoading = false; // ← loader off
    }
  });
}


  private groupByTrimester(items: CarnetItem[]) {
    // Reset both data structures
    this.carnetsByTrimester = { premiere: [], deuxieme: [], troisieme: [], annexe: [] };
    this.organizedCarnets = {
      premiere: { carnet_prive: [], carnet_etatique: [], certificat_prive: [], certificat_etatique: [], bulletin: [] },
      deuxieme: { carnet_prive: [], carnet_etatique: [], certificat_prive: [], certificat_etatique: [], bulletin: [] },
      troisieme: { carnet_prive: [], carnet_etatique: [], certificat_prive: [], certificat_etatique: [], bulletin: [] },
      annexe: { annexe: []}
    };

    for (const item of items) {
      const trimesterKey = item.trimestre || 'annexe';
      const typeKey = item.type;

      // Add to trimester groups (existing functionality)
      if (!this.carnetsByTrimester[trimesterKey]) this.carnetsByTrimester[trimesterKey] = [];
      this.carnetsByTrimester[trimesterKey].push(item);

      // Add to organized structure by type
      if (this.organizedCarnets[trimesterKey] && this.organizedCarnets[trimesterKey][typeKey]) {
        this.organizedCarnets[trimesterKey][typeKey].push(item);
      } else if (trimesterKey === 'annexe') {
        this.organizedCarnets[trimesterKey]['annexe'].push(item);
      }
    }
  }

  openModal(item: CarnetItem) {
    this.modalItem = item;
    this.showModal = true;
    this.modalHtml = null;
    this.modalError = null;
    this.modalLoading = true;
    const s = this.selectedStudent;
    if (!s) { this.modalLoading = false; return; }
    const params = this.buildShowCarnetParams(item, s);
    if (!params) {
      this.modalError = 'Impossible de récupérer le document (paramètres incomplets).';
      this.modalLoading = false;
      return;
    }
    this.carnetService.getCarnetHtml(params).subscribe({
      next: (html) => {
        this.modalHtml = this.sanitizer.bypassSecurityTrustHtml(html || '');
        this.modalLoading = false;
      },
      error: () => {
        this.modalError = 'Impossible de charger le contenu du carnet.';
        this.modalLoading = false;
      }
    });
  }
  closeModal() {
    this.showModal = false;
    this.modalItem = null;
    this.modalHtml = null;
    this.modalError = null;
    this.modalLoading = false;
  }

  getModalTitle(item: CarnetItem | null): string {
    if (!item) return '';
    
    // Normalize type string just in case
    const type = String(item.type).toLowerCase();

    if (type.includes('carnet_prive')) return 'Carnet Privé';
    if (type.includes('carnet_etatique')) return 'Carnet Étatique';
    if (type.includes('certificat_prive')) return 'Certificat Privé';
    if (type.includes('certificat_etatique')) return 'Certificat Étatique';
    if (type.includes('annexe_prive')) return 'Annexe Privée';
    if (type.includes('annexe_etatique')) return 'Annexe Étatique';
    
    // Fallback for generic types if they exist
    if (type === 'annexe') return 'Annexe';
    
    return item.name;
  }

  downloadFile(path: string) {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    const baseUrl = 'https://plateforme.educanet.pro/';
    const fileUrl = baseUrl + path.trim();
    window.open(fileUrl, '_blank');
    const fileName = this.extractFileName(path);
    if (fileName.toLowerCase().endsWith('.pdf')) {
      return;
    }
    fetch(fileUrl, { credentials: 'include' })
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP error ${response.status}`);
        }
        return response.blob();
      })
      .then(blob => {
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = fileName || 'document.pdf';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      })
      .catch(error => {
        console.warn('[CarnetComponent] Unable to fetch file for download, falling back to direct link.', error);
        const fallbackLink = document.createElement('a');
        fallbackLink.href = fileUrl;
        fallbackLink.rel = 'noopener';
        fallbackLink.download = fileName || '';
        document.body.appendChild(fallbackLink);
        fallbackLink.click();
        document.body.removeChild(fallbackLink);
      });
  }

  // Helper methods for template
  getCarnetByType(trimester: string, type: string): CarnetItem | null {
    const items = this.organizedCarnets[trimester]?.[type];
    return items && items.length > 0 ? items[0] : null;
  }

  hasCarnetType(trimester: string, type: string): boolean {
    const items = this.organizedCarnets[trimester]?.[type];
    return items && items.length > 0;
  }

  getFirstFileFromCarnet(carnet: CarnetItem | null): string | null {
    return carnet?.files && carnet.files.length > 0 ? carnet.files[0].path : null;
  }

  private buildShowCarnetParams(item: CarnetItem, student: Student):
    | {
        version: string;
        gradeId: number;
        batchId: number;
        session: string;
        idStudent: number;
        idOrg: number;
        idGuardian: number;
        type: string;
        nameOrg: string;
      }
    | null {
    const session = this.resolveSession(item);
    const type = this.resolveTypeForShowCarnet(item);
    const version = this.resolveVersion(item, type);

    const idOrg =
      student.idOrg ??
      item.idOrg ??
      item.idorg ??
      this.userData?.orgid ??
      null;

    const idGuardian =
      student.idGuardian ??
      item.idGuardian ??
      item.idguardian ??
      this.userData?.idGuardian ??
      null;

    if (
      session == null ||
      type == null ||
      version == null ||
      idOrg == null ||
      idGuardian == null
    ) {
      return null;
    }

    return {
      version: String(version),
      gradeId: student.gradeId,
      batchId: student.batchId,
      session: String(session),
      idStudent: student.idStudent,
      idOrg: Number(idOrg),
      idGuardian: Number(idGuardian),
      type,
      nameOrg: encodeURIComponent(student.nameOrg || '')
    };
  }

  private resolveSession(item: CarnetItem): string | number | null {
    // Primary mapping based on trimester
    switch (item.trimestre) {
      case 'premiere':
        return 0;
      case 'deuxieme':
        return 1;
      case 'troisieme':
        return 2;
    }

    const anyItem = item as any;
    if (item.session != null) return item.session;
    if (anyItem.Session != null) return anyItem.Session;
    if (anyItem.sessionvl != null) return anyItem.sessionvl;

    return null;
  }

  private resolveVersion(item: CarnetItem, type: string | null): string | null {
    const anyItem = item as any;
    if (item.type) {
      const lowered = item.type.toLowerCase();
      if (lowered.includes('etatique')) {
        return '1';
      }
      if (lowered.includes('prive')) {
        return '2';
      }
    }
    if (item.version) return String(item.version);
    if ((item as any).Version) return String((item as any).Version);

    if (type === 'carnet') {
      if (anyItem.carnetvl) return String(anyItem.carnetvl);
      if (anyItem.carnet) return String(anyItem.carnet);
      if (anyItem.carnetv1) return String(anyItem.carnetv1);
      if (anyItem.carnetv2) return String(anyItem.carnetv2);
    } else if (type === 'certificat') {
      if (anyItem.certifvl) return String(anyItem.certifvl);
      if (anyItem.certificat) return String(anyItem.certificat);
      if (anyItem.certificatv1) return String(anyItem.certificatv1);
      if (anyItem.certificatv2) return String(anyItem.certificatv2);
    } else if (type === 'bulletin') {
      if (anyItem.bulletinvl) return String(anyItem.bulletinvl);
      if (anyItem.bulletin) return String(anyItem.bulletin);
    } else if (type === 'annexe') {
      if (anyItem.annexe) return String(anyItem.annexe);
      if (anyItem.annexevl) return String(anyItem.annexevl);
    }

    const rawType =
      item.type ||
      (anyItem.typeDocument ? String(anyItem.typeDocument) : '') ||
      (anyItem.Type ? String(anyItem.Type) : '');

    if (rawType.includes('etatique')) {
      return '1';
    }
    if (rawType.includes('prive')) {
      return '2';
    }

    return null;
  }

  private resolveTypeForShowCarnet(item: CarnetItem): string | null {
    if (item.type) {
      if (item.type.includes('carnet')) return 'carnet';
      if (item.type.includes('certificat')) return 'certificat';
      if (item.type.includes('annexe')) return 'annexe';
      if (item.type.includes('bulletin')) return 'bulletin';
    }
    if ((item as any).typeDocument) {
      return String((item as any).typeDocument);
    }
    if ((item as any).Type) {
      return String((item as any).Type);
    }
    return null;
  }

  private extractFileName(path: string): string {
    return path?.split('/').pop() || '';
  }
}
